import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Downshift from 'downshift';
import intlShape from '@/shared/utils/intlShape';

import { injectStateService, StateService } from '@/shared/services/StateService';
import RelationName from '@/shared/components/RelationName';
import SearchRelation from '@/shared/components/SearchRelation';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import Item from '@/shared/components/input/select/Item';

import messages from '../dashboardMessages';

const itemToString = i => (i ? `${i.name} ${i.firstName}` : '');

const DropDownMenu = ({
  loading,
  relations,
  error,
  highlightedIndex,
  selectedItem,
  getItemProps,
  getMenuProps,
}) => {
  let innerComponent = null;
  if (loading) innerComponent = <LoadingAnimation />;
  if (error) innerComponent = <FormattedMessage {...messages.ErrorLoadingData} />;
  if (!innerComponent && (!relations || !relations.length)) return null;
  return (
    <div className="Select--relative open width100pct">
      <ul
        {...getMenuProps({
          className: 'Select--dropdown dropdown-menu width100pct dropdown-menu-right width100pct',
        })}
      >
        {innerComponent ||
          relations.map((relation, index) => (
            <Item
              key={relation.id}
              item={relation}
              index={index}
              isSelected={highlightedIndex === index}
              isActive={selectedItem === relation}
              getItemProps={getItemProps}
            >
              <RelationName relation={relation} enabled={false} />
            </Item>
          ))}
      </ul>
    </div>
  );
};

const SearchRelationDashboard = ({ intl, stateService }) => {
  const [inputValue, setInputValue] = useState('');

  const handleStateChange = useCallback(
    (changes, { clearSelection }) => {
      if (typeof changes.selectedItem !== 'undefined') {
        if (changes.selectedItem && changes.selectedItem.id) {
          stateService.gotoRelation(changes.selectedItem.id);
        }
        clearSelection();
      } else if (typeof changes.inputValue !== 'undefined') {
        setInputValue(changes.inputValue);
      }
    },
    [stateService],
  );

  return (
    <div className="search-block">
      <div className="search-box">
        <div className="content">
          <Downshift itemToString={itemToString} onStateChange={handleStateChange}>
            {({
              getInputProps,
              getMenuProps,
              isOpen,
              clearItems,
              setItemCount,
              highlightedIndex,
              getItemProps,
              selectedItem,
            }) => (
              <div>
                <label className="sr-only" htmlFor="dashboard-search-relation">
                  {intl.formatMessage(messages.SearchRelation)}
                </label>
                <input
                  {...getInputProps({
                    placeholder: intl.formatMessage(messages.SearchRelation),
                    className: 'form-control',
                    name: 'search',
                    autoComplete: 'off',
                    maxLength: '128',
                    id: 'dashboard-search-relation',
                    type: 'search',
                    onKeyDown(e) {
                      if (e.key === 'Enter' && highlightedIndex === null && e.target.value) {
                        stateService.gotoState('relations.overview', {
                          relationName: e.target.value,
                        });
                        return false;
                      }
                    },
                  })}
                />
                {!isOpen ? null : (
                  <SearchRelation
                    searchValue={inputValue}
                    onLoaded={({ relations }) => {
                      clearItems();
                      if (relations) {
                        setItemCount(relations.length);
                      }
                    }}
                  >
                    {({ loading, relations, error }) => (
                      <DropDownMenu
                        {...{
                          loading,
                          relations,
                          error,
                          highlightedIndex,
                          selectedItem,
                          getItemProps,
                          getMenuProps,
                        }}
                      />
                    )}
                  </SearchRelation>
                )}
              </div>
            )}
          </Downshift>
        </div>
      </div>
    </div>
  );
};
SearchRelationDashboard.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(SearchRelationDashboard));
